import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { Page } from 'common/components/Page';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchApplicationSuites } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { ActionDefinition, ColumnDefinition, ColumnType,Operations } from 'common/types/TypeDefs';

import { ApplicationSuitesDeleteDialogContent } from './ApplicationSuiteDeleteDialogContent';
import { ApplicationSuitesCreateContent } from './ApplicationSuitesEditDrawerContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters,
} from './applicationSuitesSlice';

export function ApplicationSuites() {
    const typeName = 'Application Suite';
    const [processing, setProcessing] = useState<boolean>(false);

    const shouldDisableEditButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);

    const [sortModel, setSortModel] = usePalmSortModel();

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { },
        fetchApplicationSuites,
        setProcessing
    );

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${id}/${Routes.Tenants}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableEditButtons },
        { action: Operations.Delete, disabled: shouldDisableEditButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'Application Suite ID', columnType: ColumnType.First, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Name', columnType: ColumnType.Tooltip },
        { columnId: 'tenantsCount', columnName: 'Tenants', columnType: ColumnType.Tooltip },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return (
        <Page
            title={RoutesNameMap.get(Routes.ApplicationSuites)}
            header={(
                <Typography variant="h5" gutterBottom component="div" marginTop={'1rem'}>
                    {RoutesNameMap.get(Routes.ApplicationSuites)}
                </Typography>
            )}
        >
            <PalmDataGrid
                actionDefinitions={actionDefinitions}
                columnDefinitions={columnDefinitions}
                contentFunction={ApplicationSuitesCreateContent}
                deleteFunction={ApplicationSuitesDeleteDialogContent}
                title={typeName}
                getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
                isCreateButtonDisabled={shouldDisableEditButtons}
                filter={filterProp}
                processing={processing}
                refreshDataGridClick={() => fetchEntities()}
                sortModel={sortModel}
                clientSortHandler={setSortModel}
                shouldShowExportButton={true}
            />
        </Page>
    );
}
